import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Grid,
  Typography,
  LinearProgress,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  SvgIcon,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Info } from "@mui/icons-material";

import { StyledMainContainer } from "./styled";
import { setSnackbar } from "../../context/redux/reducers/snackbar";

import { colors } from "../../theme/colors";

import AddIcon from "@mui/icons-material/Add";

import reclamaTusPremios from "../../img/reclama-tus-premioswebp.webp";

const isValidPrize = (prize) =>
  prize !== null &&
  prize !== undefined &&
  prize.PremioId !== null &&
  prize.PremioId !== undefined &&
  prize.NombreRecompensa !== null &&
  prize.NombreRecompensa !== undefined &&
  prize.NombreRecompensa !== "undefined" &&
  prize.PorcentajeAvance !== null &&
  prize.PorcentajeAvance !== undefined;

const filterValidPrizes = (arr) => (arr || []).filter(isValidPrize);

const PrizeCard = ({ prize, onClaimPrize }) => {
  const isCompleted = prize.PorcentajeAvance === 1;
  const isClaimed = prize.Estatus === 1;
  const isBlocked = prize.Estatus === 2;

  const handleClaimClick = () => {
    if (isCompleted && !isClaimed && !isBlocked) {
      onClaimPrize(prize.PremioId);
    }
  };

  const displayPercentage = prize.PorcentajeAvance * 100;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "8px",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            color: colors.primary,
            fontWeight: "bold",
            fontSize: "16px",
            marginBottom: "4px",
          }}
        >
          {prize.NombreRecompensa}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.gray.textLight,
            fontSize: "14px",
            marginBottom: "8px",
          }}
        >
          {prize.DescripcionRecompensa}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={displayPercentage}
            sx={{
              flex: 1,
              height: "8px",
              borderRadius: "4px",
              backgroundColor: "#e0e0e0",
              maxWidth: "360px",
              "& .MuiLinearProgress-bar": {
                backgroundColor: isCompleted
                  ? colors.green.secondary
                  : colors.primary,
                borderRadius: "4px",
              },
            }}
          />
        </Box>
      </Box>
      <Box sx={{ marginLeft: "16px" }}>
        <Button
          variant="contained"
          disabled={!isCompleted || isBlocked || isClaimed}
          onClick={handleClaimClick}
          className={
            isCompleted && !isClaimed && !isBlocked
              ? "Btn-reclamar-premio-retos"
              : ""
          }
          sx={{
            backgroundColor: isCompleted
              ? colors.green.secondary
              : colors.primary,
            color: colors.white.main,
            marginTop: "10px",
            border: "1px solid transparent",
            borderRadius: "8px",
            padding: { xs: "8px 8px", md: "8px 3rem" },
            fontSize: { xs: "10px", md: "14px" },
            fontWeight: "bold",
            minWidth: { xs: "120px", md: "220px" },
            boxShadow: "none",
            borderColor: isClaimed ? colors.green.secondary : colors.white.main,
            "&:disabled": {
              backgroundColor: isClaimed ? colors.white.main : "#e0e0e0",
              color: isClaimed
                ? colors.green.secondary
                : colors.white.main
                  ? isBlocked
                  : colors.gray.main,
            },
            "&:hover": {
              backgroundColor:
                isCompleted && !isClaimed && !isBlocked
                  ? colors.green.primary
                  : undefined,
            },
          }}
        >
          {isClaimed
            ? "Premio reclamado"
            : isBlocked
              ? "Bloqueado"
              : isCompleted
                ? "Reclamar premio"
                : "En progreso"}
        </Button>
      </Box>
    </Box>
  );
};

export default function Prizes() {
  const [searchParams] = useSearchParams();
  const { monthNumber: urlMonthNumber } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prizesData, setPrizesData] = useState(null);
  const [activeTab, setActiveTab] = useState("actuales");
  const [selectedMonth, setSelectedMonth] = useState(
    parseInt(urlMonthNumber) ||
      parseInt(searchParams.get("m")) ||
      new Date().getMonth() + 1,
  );
  const [currentYear] = useState(2025);

  const monthNumber = urlMonthNumber || searchParams.get("m") || selectedMonth;

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const getAvailableMonths = () => {
    const currentMonth = new Date().getMonth() + 1;
    const availableMonths = [];

    for (let i = 0; i < 6; i++) {
      let monthIndex = currentMonth - i;

      if (monthIndex <= 0) {
        monthIndex += 12;
      }

      availableMonths.push({
        value: monthIndex,
        label: monthNames[monthIndex - 1],
      });
    }

    return availableMonths;
  };

  const claimPrize = async (prizeId) => {
    try {
      setLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_BE_URL}/rtm/claim`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            prizeId: prizeId,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        let errorMessage = "Error al reclamar el premio";

        if (
          data.result &&
          Array.isArray(data.result) &&
          data.result.length > 0
        ) {
          errorMessage =
            data.result[0].Mensaje || data.errorMessage || errorMessage;
        } else if (data.errorMessage) {
          errorMessage = data.errorMessage;
        }

        dispatch(setSnackbar(true, "error", errorMessage, <Info />));
        setLoading(false);
        return;
      }

      if (data.error) {
        let errorMessage = "Error al reclamar el premio";

        if (
          data.result &&
          Array.isArray(data.result) &&
          data.result.length > 0
        ) {
          errorMessage =
            data.result[0].Mensaje || data.errorMessage || errorMessage;
        } else if (data.errorMessage) {
          errorMessage = data.errorMessage;
        }

        dispatch(setSnackbar(true, "error", errorMessage, <Info />));
        setLoading(false);
        return;
      }

      fetchPrizesData();

      dispatch(
        setSnackbar(true, "success", "Premio reclamado exitosamente", <Info />),
      );
      console.log("Premio reclamado exitosamente");
    } catch (err) {
      console.error("Error claiming prize:", err);
      dispatch(
        setSnackbar(
          true,
          "error",
          err.message || "ERROR de conexión",
          <Info />,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPrizesData = () => {
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_BE_URL}/rtm/prizes?m=${monthNumber}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
      .then((res) => {
        if (res.status === 403) {
          throw new Error("No tienes permisos para acceder a esta información");
        }
        return res.json();
      })
      .then((data) => {
        if (data.error) {
          throw new Error(
            data.errorMessage || "Error en la respuesta del servidor",
          );
        }

        const normalizedData = {
          completed: filterValidPrizes(data.result?.completed),
          on_course: {
            claimed: filterValidPrizes(data.result?.on_course?.claimed),
            not_claimed: filterValidPrizes(data.result?.on_course?.not_claimed),
          },
        };

        setPrizesData(normalizedData);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching prizes data:", err);
        setError(err.message);
        setLoading(false);
      });
  };

  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);

    navigate(`/prizes/${newMonth}`);
  };

  useEffect(() => {
    if (monthNumber) {
      fetchPrizesData();

      if (urlMonthNumber) {
        setSelectedMonth(parseInt(urlMonthNumber));
      }
    }
  }, [monthNumber, urlMonthNumber]);

  const getCurrentPrizes = () => {
    if (!prizesData) return [];

    if (activeTab === "actuales") {
      const completed = prizesData.completed || [];
      const claimed = prizesData.on_course?.claimed || [];
      const notClaimed = prizesData.on_course?.not_claimed || [];

      return [...completed, ...claimed, ...notClaimed];
    } else {
      return prizesData.completed || [];
    }
  };

  if (loading) {
    return (
      <StyledMainContainer style={{ justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <CircularProgress sx={{ color: "#4fc3d7" }} />
          <Typography sx={{ color: "white" }}>
            Cargando recompensas...
          </Typography>
        </div>
      </StyledMainContainer>
    );
  }

  if (error) {
    return (
      <StyledMainContainer style={{ justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            width: "100%",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <Typography sx={{ color: "white", textAlign: "center" }}>
            Error al cargar las premios: {error}
          </Typography>
          <Button
            onClick={() => navigate("/")}
            variant="contained"
            sx={{
              backgroundColor: "#4fc3d7",
              color: "white",
              "&:hover": {
                backgroundColor: "#3fa8bb",
              },
            }}
          >
            Reintentar
          </Button>
        </div>
      </StyledMainContainer>
    );
  }

  const currentPrizes = getCurrentPrizes();

  return (
    <StyledMainContainer
      white
      style={{ flexDirection: "column", gap: "2rem", alignItems: "center" }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          maxWidth: "900px",
          alignItems: "center",
          padding: { xs: "0 32px ", md: "0" },
        }}
      >
        <Grid item xs={12} md={5}>
          <Box
            component="img"
            src={reclamaTusPremios}
            alt="Test Financiero - Banco Industrial"
            sx={{
              width: "100%",
              height: "auto",
              marginLeft: {
                md: "8rem",
                xs: "0rem",
              },
              maxWidth: {
                xs: "184px",
              },
              objectFit: "contain",
              display: "block",
            }}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <Typography
            variant="h4"
            sx={{
              color: colors.primary,
              fontWeight: "bold",
              margin: 0,
              fontSize: "28px",
            }}
          >
            Reclama tus premios
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: colors.secondary,
              fontWeight: "bold",
              margin: 0,
              fontSize: "22px",
            }}
          >
            Presionando los botones de abajo
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: colors.gray.textLight,
              fontWeight: "bold",
              margin: 0,
              fontSize: "1rem",
            }}
          >
            Cumple tus retos cada mes y recibe las recompensas.
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        sx={{
          maxWidth: "900px",
          alignItems: "center",
          padding: { xs: "0 32px ", md: "0" },
        }}
      >
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-start",
              marginTop: "2rem",
              marginLeft: {
                md: "8rem",
                xs: "0rem",
              },
              flexDirection: { xs: "column", md: "row" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Button
              variant={activeTab === "actuales" ? "contained" : "outlined"}
              onClick={() => setActiveTab("actuales")}
              sx={{
                backgroundColor:
                  activeTab === "actuales" ? colors.primary : "transparent",
                color: activeTab === "actuales" ? "white" : colors.primary,
                borderColor: colors.primary,
                fontWeight: "bold",
                fontSize: "14px",
                padding: "8px 24px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor:
                    activeTab === "actuales" ? colors.secondary : "#B2ECF2",
                  borderColor: "#B2ECF2",
                },
              }}
            >
              Premios actuales
            </Button>
            <Button
              variant={activeTab === "canjeados" ? "contained" : "outlined"}
              onClick={() => setActiveTab("canjeados")}
              sx={{
                backgroundColor:
                  activeTab === "canjeados" ? colors.primary : "transparent",
                color: activeTab === "canjeados" ? "white" : colors.primary,
                borderColor: colors.primary,
                fontWeight: "bold",
                fontSize: "14px",
                padding: "8px 24px",
                borderRadius: "8px",
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor:
                    activeTab === "canjeados" ? colors.secondary : `#B2ECF2`,
                  borderColor: "#B2ECF2",
                },
                marginTop: { xs: "0.5rem", md: "0rem" },
                marginBottom: { xs: "1rem", md: "0rem" },
              }}
            >
              Premios canjeados
            </Button>

            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 120,
                marginLeft: { xs: 0, md: 1 },
              }}
            >
              <InputLabel
                sx={{
                  color: colors.primary,
                  "&.Mui-focused": {
                    color: colors.primary,
                  },
                }}
              >
                Mes
              </InputLabel>
              <Select
                value={selectedMonth}
                onChange={handleMonthChange}
                label="Mes"
                sx={{
                  color: colors.primary,
                  borderRadius: "8px",
                  fontSize: "17px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.primary,
                  },
                  width: {
                    md: "180px",
                  },
                  marginTop: { xs: "1rem", md: "0rem" },
                }}
              >
                {getAvailableMonths().map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {activeTab === "actuales" && (
            <Box
              sx={{
                py: 2,
                marginLeft: {
                  md: "8rem",
                  xs: "0rem",
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: colors.secondary,
                  fontWeight: "700",
                  mb: 1,
                  fontSize: "1rem",
                }}
              >
                {monthNames[parseInt(monthNumber) - 1]} {currentYear}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.primary, mb: 2 }}>
                Premios Actuales
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {currentPrizes.length > 0 ? (
                  currentPrizes.map((prize, index) => (
                    <PrizeCard
                      key={prize.PremioId || index}
                      prize={prize}
                      onClaimPrize={claimPrize}
                    />
                  ))
                ) : (
                  <Typography
                    sx={{
                      color: colors.gray.textLight,
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    No hay premios disponibles para este mes
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {activeTab === "canjeados" && (
            <Box
              sx={{
                py: 2,
                marginLeft: {
                  md: "8rem",
                  xs: "0rem",
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: colors.secondary,
                  fontWeight: "700",
                  mb: 1,
                  fontSize: "1rem",
                }}
              >
                {monthNames[selectedMonth - 1]} {currentYear}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.primary, mb: 2 }}>
                Premios Canjeados - {monthNames[selectedMonth - 1]}{" "}
                {currentYear}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {currentPrizes.length > 0 ? (
                  currentPrizes.map((prize, index) => (
                    <PrizeCard
                      key={prize.PremioId || index}
                      prize={prize}
                      onClaimPrize={claimPrize}
                    />
                  ))
                ) : (
                  <Typography
                    sx={{
                      color: colors.gray.textLight,
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    No hay premios canjeados para este mes
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </StyledMainContainer>
  );
}
